import { STUDIO_API_BASE_URL } from '../api/studioClient';

export interface CelebrityProfile {
  slug: string;
  displayName: string;
  fullName?: string;
  gender?: string;
  category?: string;
  subCategories?: string[];
  country?: string;
  birth: {
    date: string;
    time: string | null;
    timeUnknown: boolean;
    placeName: string;
    latitude?: number;
    longitude?: number;
    coordinates?: {
      latitude?: number;
      longitude?: number;
    };
    timezone?: string | {
      offsetHours?: number;
      raw?: string | null;
      abbreviation?: string | null;
      note?: string | null;
    };
    roddenRating?: string;
    sourceType?: string;
    sourceUrl?: string;
    sourceNote?: string;
    confidenceScore?: number;
  };
  astroMeta?: {
    defaultAyanamsha?: string;
    hasAccurateBirthTime?: boolean;
    allowedForPractice?: boolean;
    chartPracticeQuality?: string;
  };
  stats?: {
    eventCount?: number;
  };
  visibility?: {
    isPublic?: boolean;
    isPremium?: boolean;
  };
}

export interface CelebrityLifeEvent {
  id: string;
  celebritySlug: string;
  celebrityName?: string;
  eventType: string;
  eventTitle: string;
  eventDescription: string;
  eventDate: {
    date: string;
    year?: number;
    month?: number | null;
    day?: number | null;
    dateAccuracy?: string;
  };
  astrologyUse?: {
    importance?: string;
    practiceTags?: string[];
    suggestedTechniques?: string[];
  };
  source?: {
    sourceName?: string;
    sourceUrl?: string;
    verified?: boolean;
  };
}

const SOURCE_TIMEZONE_OVERRIDES: Record<string, number> = {
  // The imported raw field says LMT, but the source horoscope chart for this
  // record uses Time Zone 2.0; that is required for the expected Virgo Lagna.
  'adolf-hitler': 120
};

export const fetchCelebrityProfiles = async () => {
  const response = await fetch(`${STUDIO_API_BASE_URL}/chart-database/profiles`);
  const json = await response.json().catch(() => ({}));
  if (!response.ok || !json.success) {
    throw new Error(json.message || 'Unable to load practice charts.');
  }
  return {
    profiles: (json.data?.profiles || []) as CelebrityProfile[],
    categories: (json.data?.categories || []) as string[]
  };
};

export const getBirthTimeParts = (profile: CelebrityProfile) => {
  const time = profile.birth.time;
  if (!time) {
    return null;
  }

  const [hour = '0', minute = '0', second = '0'] = time.split(':');
  return {
    hour: Number(hour) || 0,
    minute: Number(minute) || 0,
    second: Number(second) || 0
  };
};

export const formatCelebrityEventDate = (event: CelebrityLifeEvent) => {
  const { year, month, day, dateAccuracy } = event.eventDate;
  if (year && month && day) {
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  }
  if (year && month) {
    return `${String(month).padStart(2, '0')}/${year}`;
  }
  if (year) return String(year);
  return `${event.eventDate.date}${dateAccuracy ? ` (${dateAccuracy})` : ''}`;
};

const parseSourceTimezoneOffset = (raw?: string | null) => {
  const value = String(raw || '').trim();
  if (!value) return null;

  if (/\bLST\b/i.test(value) && /is standard time/i.test(value)) {
    return 330;
  }

  const gmt = value.match(/^GMT\s*([+-])?\s*(\d{1,2})?(?::?(\d{2}))?/i);
  if (gmt) {
    const hours = Number(gmt[2] || 0);
    const minutes = Number(gmt[3] || 0);
    const sign = gmt[1] === '-' ? -1 : 1;
    return sign * ((hours * 60) + minutes);
  }

  const hourOffset = value.match(/\bh(\d{1,2})([ew])(\d{1,2})?/i);
  if (hourOffset) {
    const sign = hourOffset[2].toLowerCase() === 'w' ? -1 : 1;
    return sign * ((Number(hourOffset[1]) * 60) + Number(hourOffset[3] || 0));
  }

  const meanOffset = value.match(/\bm(\d{1,3})([ew])(\d{1,2})?/i);
  if (meanOffset) {
    const sign = meanOffset[2].toLowerCase() === 'w' ? -1 : 1;
    const degrees = Number(meanOffset[1]) + (Number(meanOffset[3] || 0) / 60);
    return Math.round(sign * degrees * 4);
  }

  return null;
};

const getSourceTimezone = (profile: CelebrityProfile) => {
  const override = SOURCE_TIMEZONE_OVERRIDES[profile.slug];
  if (typeof override === 'number') {
    return { minutes: override, isSourceFixed: true };
  }

  const timezone = profile.birth.timezone;
  if (typeof timezone === 'object' && timezone) {
    const offsetHours = Number(timezone.offsetHours);
    if (Number.isFinite(offsetHours)) {
      return { minutes: Math.round(offsetHours * 60), isSourceFixed: true };
    }

    const parsed = parseSourceTimezoneOffset(timezone.raw || timezone.note || timezone.abbreviation);
    if (parsed !== null) {
      return { minutes: parsed, isSourceFixed: true };
    }
  }

  if (profile.birth.timezone === 'Asia/Kolkata') {
    return { minutes: 330, isSourceFixed: true };
  }

  return { minutes: 330, isSourceFixed: false };
};

const getAyanamshaMode = (profile: CelebrityProfile) => {
  const mode = String(profile.astroMeta?.defaultAyanamsha || 'LAHIRI').trim().toUpperCase();
  return mode || 'LAHIRI';
};

export const buildCelebrityChartPayload = (profile: CelebrityProfile) => {
  const time = getBirthTimeParts(profile);
  if (!time) {
    throw new Error('Birth time is not available for this chart yet.');
  }

  const [city = profile.birth.placeName, state = '', country = profile.country || ''] = profile.birth.placeName
    .split(',')
    .map((part) => part.trim());
  const latitude = profile.birth.latitude ?? profile.birth.coordinates?.latitude;
  const longitude = profile.birth.longitude ?? profile.birth.coordinates?.longitude;
  const timezone = getSourceTimezone(profile);

  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    throw new Error('Birth coordinates are not available for this chart yet.');
  }

  return {
    name: profile.displayName,
    dateOfBirth: profile.birth.date,
    timeOfBirth: {
      hour: time.hour,
      minute: time.minute,
      second: time.second
    },
    placeOfBirth: {
      city,
      state,
      country,
      latitude,
      longitude,
      timezone: timezone.minutes,
      timezoneSource: timezone.isSourceFixed ? 'chart_database' : undefined
    },
    gender: profile.gender === 'female' ? 'Female' : profile.gender === 'male' ? 'Male' : 'Other',
    saveChart: false,
    saveAnonymized: false,
    createdFrom: 'chart_database',
    calculationModule: 'nadi',
    ayanamshaMode: getAyanamshaMode(profile),
    chartSettings: {
      chartStyle: 'North',
      language: 'Hinglish',
      nodeMode: 'true'
    }
  };
};
